import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { BookOpen, Plane, Home, Loader2, CheckCircle, MapPin, Car, TrendingUp } from 'lucide-react';
import { tokenApi, authApi, analyticsApi } from '@/lib/api';
import { toast } from 'sonner';
import { isAuthenticated, setAuthData, getGeolocation, getModulePath, getModuleName } from '@/lib/utils';
import { PWAInstallPrompt } from '@/components/PWAInstallPrompt';

const moduleIcons = {
  notes: BookOpen,
  travel: Plane,
  home: Home,
  car: Car,
  finance: TrendingUp,
};

const moduleColors = {
  notes: { bg: 'bg-blue-100', text: 'text-blue-600', primary: '#2563EB' },
  travel: { bg: 'bg-teal-100', text: 'text-teal-600', primary: '#0D9488' },
  home: { bg: 'bg-emerald-100', text: 'text-emerald-600', primary: '#10B981' },
  car: { bg: 'bg-orange-100', text: 'text-orange-600', primary: '#E97316' },
  finance: { bg: 'bg-violet-100', text: 'text-violet-600', primary: '#8B5CF6' },
};

export default function TokenActivation({ moduleType }) {
  const { tokenId } = useParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('loading'); // loading, auth_required, activating, success, error
  const [tokenInfo, setTokenInfo] = useState(null);
  const [error, setError] = useState('');
  const [showAuth, setShowAuth] = useState(false);
  const [email, setEmail] = useState('');
  const [isAuthLoading, setIsAuthLoading] = useState(false);

  const Icon = moduleIcons[moduleType] || BookOpen;
  const colors = moduleColors[moduleType] || moduleColors.notes;

  useEffect(() => {
    checkAndActivate();
  }, [tokenId]);

  const checkAndActivate = async () => {
    try {
      // First check if token exists
      const tokenResponse = await tokenApi.getToken(tokenId);
      setTokenInfo(tokenResponse.data);

      // Log scan event
      const location = await getGeolocation();
      await analyticsApi.logEvent(tokenId, 'token_scan', location || {});

      // If user is authenticated, try to activate
      if (isAuthenticated()) {
        await activateToken();
      } else {
        // If token is already activated by someone, show message
        if (tokenResponse.data.status === 'activated' && tokenResponse.data.linked_user_id) {
          setError('This artifact is already activated by another user.');
          setStatus('error');
        } else {
          setStatus('auth_required');
          setShowAuth(true);
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setError('Invalid artifact. This token does not exist.');
      } else {
        setError('Failed to load artifact information.');
      }
      setStatus('error');
    }
  };

  const activateToken = async () => {
    setStatus('activating');
    try {
      const location = await getGeolocation();
      await tokenApi.activateToken(tokenId, location || {});
      setStatus('success');
      toast.success('Artifact activated!');
      
      // Redirect to module after short delay
      setTimeout(() => {
        navigate(getModulePath(moduleType, tokenId));
      }, 1500);
    } catch (error) {
      if (error.response?.data?.detail?.includes('already activated')) {
        // If already activated by current user, just go to module
        navigate(getModulePath(moduleType, tokenId));
      } else {
        setError(error.response?.data?.detail || 'Failed to activate artifact');
        setStatus('error');
      }
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsAuthLoading(true);
    try {
      const response = await authApi.requestMagicLink(email);
      toast.success('Magic link sent! Check your email.');
      
      // For demo purposes, if debug_token is returned (mock mode), auto-verify
      if (response.data.debug_token) {
        toast.info('Demo mode: Auto-verifying...');
        try {
          const verifyResponse = await authApi.verifyMagicLink(response.data.debug_token);
          setAuthData(verifyResponse.data.token, verifyResponse.data.user);
          setShowAuth(false);
          await activateToken();
        } catch (e) {
          toast.error('Auto-verification failed');
        }
      }
    } catch (error) {
      toast.error('Failed to send magic link');
    } finally {
      setIsAuthLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="token-activation-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm max-w-md w-full text-center"
      >
        {/* Module Icon */}
        <div className={`w-20 h-20 ${colors.bg} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
          <Icon className={`w-10 h-10 ${colors.text}`} />
        </div>

        {/* Status: Loading */}
        {status === 'loading' && (
          <>
            <Loader2 className="w-8 h-8 text-slate-400 mx-auto mb-4 animate-spin" />
            <h1 className="text-xl font-bold text-slate-900 mb-2">Loading Artifact</h1>
            <p className="text-slate-600">Please wait...</p>
          </>
        )}

        {/* Status: Auth Required */}
        {status === 'auth_required' && (
          <>
            <h1 className="text-xl font-bold text-slate-900 mb-2">{getModuleName(moduleType)}</h1>
            <p className="text-slate-600 mb-2">Token: <span className="font-mono">{tokenId}</span></p>
            <p className="text-slate-500 text-sm mb-6">Sign in to activate this artifact</p>
            <Button 
              onClick={() => setShowAuth(true)}
              style={{ backgroundColor: colors.primary }}
              className="text-white rounded-full px-8 hover:opacity-90"
              data-testid="activate-signin-btn"
            >
              Sign In to Activate
            </Button>
          </>
        )}

        {/* Status: Activating */}
        {status === 'activating' && (
          <>
            <Loader2 className="w-8 h-8 text-slate-400 mx-auto mb-4 animate-spin" />
            <h1 className="text-xl font-bold text-slate-900 mb-2">Activating...</h1>
            <p className="text-slate-600">Setting up your artifact</p>
          </>
        )}

        {/* Status: Success */}
        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200 }}
            >
              <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            </motion.div>
            <h1 className="text-xl font-bold text-slate-900 mb-2">Artifact Activated!</h1>
            <p className="text-slate-600">Redirecting to your {getModuleName(moduleType)}...</p>
          </>
        )}

        {/* Status: Error */}
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">!</span>
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2">Activation Failed</h1>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="rounded-full"
            >
              Go to Home
            </Button>
          </>
        )}
      </motion.div>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Activate Your Artifact</DialogTitle>
            <DialogDescription>
              Enter your email to receive a magic link and activate your {getModuleName(moduleType)}.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleMagicLink} className="space-y-4 mt-4">
            <Input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-slate-50 border-slate-200 rounded-xl h-12"
              data-testid="activation-email-input"
              required
            />
            <Button 
              type="submit"
              style={{ backgroundColor: colors.primary }}
              className="w-full text-white rounded-full hover:opacity-90"
              disabled={isAuthLoading}
              data-testid="activation-magic-link-btn"
            >
              {isAuthLoading ? 'Sending...' : 'Send Magic Link'}
            </Button>
          </form>
          
          <p className="text-xs text-center text-slate-500 mt-4">
            No account needed. We'll create one automatically.
          </p>
        </DialogContent>
      </Dialog>

      {/* PWA Install Prompt - shows after authentication */}
      {isAuthenticated() && <PWAInstallPrompt />}
    </div>
  );
}
